"use client";

import React, { useRef, useState, useEffect, useCallback } from "react";
import { X } from "lucide-react";
import { useT } from "@/hooks/useT";

// Floating draggable picture-in-picture iframe for Twitch / Kick /
// YouTube livestreams on the SportsEventClient surface. Distinct from
// the sports2/components/LivestreamPiP variant because this one
// localises the iframe title via useT() — Sports2View hard-codes
// "Livestream". Initial position is bottom-right corner.
export function LivestreamPiP({
  stream,
  onClose,
}: {
  stream: { type: string; channel: string; embedUrl: string };
  onClose: () => void;
}) {
  const t = useT();
  const dragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    setPos({ x: window.innerWidth - 408, y: window.innerHeight - 233 });
  }, []);

  const onDragStart = useCallback((e: React.MouseEvent) => {
    if (!pos) return;
    dragging.current = true;
    dragOffset.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
    const onMove = (ev: MouseEvent) => {
      if (!dragging.current) return;
      setPos({ x: ev.clientX - dragOffset.current.x, y: ev.clientY - dragOffset.current.y });
    };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp, { once: true });
  }, [pos]);

  if (!pos) return null;

  return (
    <div
      className="fixed flex flex-col rounded-[5px] shadow-2xl bg-neutral-900 border border-neutral-800 overflow-hidden select-none"
      style={{ zIndex: 99999999, left: pos.x, top: pos.y, width: 400, height: 225 }}
    >
      <div onMouseDown={onDragStart} className="absolute top-0 inset-x-0 h-8 z-10" style={{ cursor: 'grab' }} />
      <button onClick={onClose} className="absolute top-2 right-2 p-1 rounded-[5px] bg-black/60 text-white hover:bg-black/80 cursor-pointer z-20">
        <X size={16} />
      </button>
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 text-white text-[10px] font-medium uppercase z-20">
        {stream.type}
      </div>
      <div className="flex-1 w-full h-full">
        <iframe src={stream.embedUrl} className="w-full h-full border-none" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen title={t("sportsEvent.livestream")} />
      </div>
    </div>
  );
}
